import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Newspaper } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/i18n";
import { useMarket } from "@/market";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCALES = { fr: "fr-BE", nl: "nl-BE", es: "es-ES" };
const INTRO = {
  BE: "Facturation Peppol, TVA 6 %, marge de chantier, organisation du travail : nos guides pour les artisans et entrepreneurs du bâtiment en Belgique.",
  FR: "TVA à 10 % et 5,5 %, mentions obligatoires du devis, facture électronique 2026 : nos guides pour les artisans du bâtiment en France.",
  ES: "IVA reducido en reformas, presupuesto y factura conformes, Verifactu : nos guides pour les artisans du bâtiment en Espagne.",
  LU: "TVA 3 % logement, mentions légales, facture électronique : nos guides pour les artisans du bâtiment au Luxembourg.",
};

export default function BlogPage() {
  const { t, lang } = useLanguage();
  const { country } = useMarket();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = lang === "nl"
      ? "Blog & vakgidsen | Devisto"
      : "Blog & guides métiers | Devisto";
    const meta = document.querySelector('meta[name="description"]');
    const previousMeta = meta?.getAttribute("content");
    meta?.setAttribute("content", lang === "nl"
      ? "Praktische gidsen over verwarming, loodgieterij, airconditioning en warmtepompen in België: experttips, onderhoud, prijzen en beschikbare premies."
      : "Guides pratiques chauffage, plomberie, climatisation et pompes à chaleur en Belgique : conseils d'experts, entretien, prix et aides disponibles.");
    let stale = false;
    setLoading(true);
    axios.get(`${API}/blog?lang=${lang}&country=${country}`).then(({ data }) => { if (!stale) setPosts(data); }).catch(() => { if (!stale) setPosts([]); }).finally(() => { if (!stale) setLoading(false); });
    return () => {
      stale = true;
      document.title = previousTitle;
      if (meta && previousMeta) meta.setAttribute("content", previousMeta);
    };
  }, [lang, country]);

  return (
    <div data-testid="blog-page" className="font-body bg-[#F6F8FD] text-[#0F2645] min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-24">
          <Reveal>
            <p className="text-sm font-bold tracking-[0.1em] uppercase text-[#3A5CC8]">{t("Le blog Devisto")}</p>
            <h1 data-testid="blog-title" className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F2645]">
              {t("Guides & conseils de pros du bâtiment")}
            </h1>
            <p className="mt-5 text-base leading-relaxed text-[#5C6A87] max-w-2xl">
              {t(INTRO[country] || INTRO.BE)}
            </p>
          </Reveal>

          {loading ? (
            <p className="mt-16 text-sm text-[#5C6A87]">{t("Chargement…")}</p>
          ) : posts.length === 0 ? (
            <div data-testid="blog-empty" className="mt-16 rounded-2xl border border-[#E2E7F7] bg-white shadow-sm p-12 text-center">
              <Newspaper className="w-10 h-10 text-slate-300 mx-auto" />
              <p className="mt-4 text-base text-[#5C6A87]">{t("Les premiers guides arrivent très bientôt.")}</p>
            </div>
          ) : (
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, i) => (
                <Reveal key={post.id} delay={(i % 3) * 0.1}>
                  <Link
                    to={`/blog/${post.slug}`}
                    data-testid={`blog-card-${post.slug}`}
                    className="group block bg-white rounded-2xl border border-[#E2E7F7] shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden h-full"
                  >
                    {post.cover_image && (
                      <img src={post.cover_image} alt={post.title} loading="lazy" className="h-44 w-full object-cover" />
                    )}
                    <div className="p-6">
                      {post.category && (
                        <span className="rounded-full bg-[#EEF1FC] border border-blue-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#3A5CC8]">
                          {post.category}
                        </span>
                      )}
                      <h2 className="mt-3 font-heading text-xl font-semibold tracking-tight text-[#0F2645] group-hover:text-[#3A5CC8] transition-colors duration-200">
                        {post.title}
                      </h2>
                      <p className="mt-2 text-sm leading-relaxed text-[#5C6A87]">{post.excerpt}</p>
                      <p className="mt-4 text-xs text-[#5C6A87]">
                        {new Date(post.created_at).toLocaleDateString(LOCALES[lang] || "fr-BE", { day: "numeric", month: "long", year: "numeric" })}
                        <span className="inline-flex items-center gap-1 ml-3 font-semibold text-[#3A5CC8]">
                          {t("Lire")} <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
